import React, {useEffect, useRef, useState} from 'react';
import {history} from '../../../../utils/redux/store';
import {privilegeUtils} from '../../../../utils/privilegeUtils';
import {useSelector} from 'react-redux';
import useSkincare from '../../../../components/hook/useSkincare';
import Header from '../../components/header';
import Loader from '../../../../components/spinner/Loader';
import {Trans} from 'react-i18next';
import {Form, Formik} from 'formik';
import ChooseUITemplate from '../components/ChooseUITemplate';
import CustomizedLayoutSettings from './components/CustomizedLayoutSettings';
import ButtonWithRestoreGroup from '../../../../components/formik/ButtonWithRestoreGroup';
import PublishToOnlineModal from '../components/PublishToOnlineModal';
import _get from 'lodash/get';
import skincareProDao from '../../../../dao/skincareProDao';
import {toast} from 'react-toastify';
import {deviceOptions} from '../config/utils/optionsUtils';
import langCode from '../../../../utils/langUtils';

export default function SharedSimulationReportPage() {
  const auth = useSelector(state => state.components.auth.accountSummary);
  const {isV2Above} = useSkincare(auth);
  const formRef = useRef();

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [customTempDataset, setCustomTempDataset] = useState([]);
  const [generalTempInfo, setGeneralTempInfo] = useState({});
  const [selectedTempInfo, setSelectedTempInfo] = useState({});

  const [publishStatus, setPublishStatus] = useState(false);
  const [isShowPublishModal, setIsShowPublishModal] = useState(false);
  const templateType = 'SmbSimulationSharePage';

  const fetchData = async () => {
    setIsInitialLoading(true);
    try {
      const [templateLists] = await Promise.all(
        [
          skincareProDao.listUIConfigTemplates({template_type: templateType}),
        ].map(promise => promise.catch(() => ({})))
      );

      const templates = _get(templateLists, 'templates', []);
      setCustomTempDataset(templates.filter(template => !template.is_default));

      const defaultItem = templates.find(t => t.is_default);
      if (!defaultItem) {
        toast.error(
          <Trans i18nKey="toast.error.loading.data.failed">
            Failed to load data.
          </Trans>
        );
        return;
      }
      setGeneralTempInfo(defaultItem);

      const selectedItem = templates.find(t => t.selected);
      if (!selectedItem) {
        toast.error(
          <Trans i18nKey="toast.error.loading.data.failed">
            Failed to load data.
          </Trans>
        );
        return;
      }

      if (selectedItem.is_default) {
        setSelectedTempInfo({
          ...selectedItem,
          device_type: deviceOptions.map(opt => opt.value),
          langs: langCode.getConsoleStringLangList().map(opt => opt.value),
        });
      } else {
        setSelectedTempInfo(selectedItem);
      }
    } catch (error) {
      toast.error(
        <Trans i18nKey="toast.error.loading.data.failed">
          Failed to load data.
        </Trans>
      );
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleSelectedTemplate = async (formikProps, targetItem) => {
    formikProps.setFieldValue(
      'is_default',
      _get(targetItem, 'is_default', true)
    );
    formikProps.setFieldValue(
      'template_id',
      _get(targetItem, 'template_id', null)
    );
    formikProps.setFieldValue(
      'template_revision_id',
      _get(targetItem, 'template_revision_id', null)
    );
    setSelectedTempInfo(targetItem);
  };

  const handleSubmit = async targetItem => {
    try {
      const r = await skincareProDao.selectUIConfigTemplate({
        ...targetItem,
        template_type: templateType,
      });
      if (r && r.status === 'success') {
        setPublishStatus(!publishStatus);
        setIsShowPublishModal(true);
        await fetchData();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const formikConfig = {
    enableReinitialize: true,
    initialValues: (() => {
      const selectedItem = customTempDataset.find(item => item.selected) || {};
      return {
        is_default: _get(selectedItem, 'is_default', true),
        template_id: _get(selectedItem, 'template_id', null),
        template_revision_id: _get(selectedItem, 'template_revision_id', null),
      };
    })(),
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true);
      if (
        !privilegeUtils.canCustomizationAppLayout(auth) &&
        !values.is_default
      ) {
        toast.error(
          <Trans i18nKey="toast.error.update.version.failed">
            Failed to update, please try again.
          </Trans>
        );
        setSubmitting(false);
        return;
      }

      if (!values.is_default && !values.template_id) {
        toast.error(
          <Trans i18nKey="toast.error.update.template.failed">
            Failed to update, please choose a template.
          </Trans>
        );
        setSubmitting(false);
        return;
      }
      await handleSubmit(values);
      setSubmitting(false);
    },
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (
    !(
      privilegeUtils.canUiConfig(auth) ||
      privilegeUtils.isTechnicalSupport(auth) ||
      !isV2Above
    )
  ) {
    history.push('/');
  }

  return isInitialLoading ? (
    <div>
      <Header />
      <Loader />
    </div>
  ) : (
    <Formik {...formikConfig} ref={formRef}>
      {formikProps => (
        <Form className="skincare-configuration-form">
          <Header publishStatus={publishStatus} dirty={formikProps.dirty} />
          <div className="skincare-configuration-layout">
            <div className="skincare-configuration-layout__left">
              <div className="kt-portlet skincare-configuration-layout__left-portlet">
                <div className="kt-portlet__body pb-0">
                  <ChooseUITemplate
                    isDefaultLayout={_get(selectedTempInfo, 'is_default')}
                    setIsDefaultLayout={v => {
                      let targetItem = customTempDataset.find(
                        item => item.is_default === false
                      );
                      if (!targetItem) {
                        targetItem = {
                          is_default: v,
                          template_id: null,
                          template_revision_id: null,
                          device_type: deviceOptions.map(op => op.value),
                          langs: langCode
                            .getConsoleStringLangList()
                            .map(op => op.value),
                        };
                      }
                      if (v) {
                        targetItem = {
                          ...generalTempInfo,
                          is_default: true,
                          device_type: deviceOptions.map(op => op.value),
                          langs: langCode
                            .getConsoleStringLangList()
                            .map(op => op.value),
                        };
                      }
                      handleSelectedTemplate(formikProps, targetItem);
                    }}
                  />
                  <div
                    className="kt-margin-t-10 kt-margin-b-10"
                    style={{
                      backgroundColor: '#e1e1ef',
                      height: '1px',
                    }}
                  />
                </div>
                <div className="kt-portlet__body skincare-configuration-layout__left-portlet__body">
                  {_get(selectedTempInfo, 'is_default') && (
                    <div>
                      <p>
                        <Trans i18nKey="p.s.uiconfiguration.generic.ui.desc">
                          No generic configurations available for this page.
                        </Trans>
                      </p>
                    </div>
                  )}
                  {!_get(selectedTempInfo, 'is_default') && (
                    <CustomizedLayoutSettings
                      data={customTempDataset}
                      isLoading={isInitialLoading}
                      selectedTempInfo={selectedTempInfo}
                      handleSelectedTemplate={targetItem =>
                        handleSelectedTemplate(formikProps, targetItem)
                      }
                    />
                  )}
                  {privilegeUtils.canCustomizationAppLayout(auth) && (
                    <div className="d-flex justify-content-end kt-margin-t-20">
                      <ButtonWithRestoreGroup
                        saveDirty={formikProps.dirty}
                        discardButton={false}
                        isUpdating={true}
                        onRestoreClick={() => {
                          handleSubmit(generalTempInfo);
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="skincare-configuration-layout__right">
              <div className="d-flex align-items-center justify-content-center h-100">
                <Trans i18nKey="p.s.uiconfiguration.customization.no.preview.desc">
                  No preview available for this page.
                </Trans>
              </div>
            </div>
          </div>
          <PublishToOnlineModal
            isShow={isShowPublishModal}
            onPublish={isPublish => {
              formikProps.setSubmitting(false);
              formikProps.resetForm(formikProps.values);
              setPublishStatus(isPublish);
              setIsShowPublishModal(false);
            }}
          />
        </Form>
      )}
    </Formik>
  );
}
